import React, { useState, useEffect, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';
import { FONT_COLOR } from '../const/general'
import { MTR_BORDER_COLOR } from '../const/color'
import Progress from '../item/progress'

export default function LineRiver(props) {
    const [mtrData, setMtrData] = useState([]);
    const [seriesMax, setSeriesMax] = useState(0);
    const [annotations, setAnnotations] = useState({});
    const [isNoData, setIsNoData] = useState(false);
    const [annotationMax, setAnnotationMax] = useState(false);

    // チャートごとの一意のキー
    const chartId = useId();

    const aryMax = function (a, b) { return Math.max(a, b); }

    const getAnnotationsMax = () => {
        if (props.maxValInfo < 1) {
            return 5
        }
        else if (props.maxValInfo.mtrMax > props.maxValInfo.borderPurpleMax) {
            return props.maxValInfo.mtrMax + 0.5;
        }
        else if (props.maxValInfo.borderPurpleMax > props.maxValInfo.mtrMax) {
            return props.maxValInfo.borderPurpleMax + 0.1;
        }
        return 5;
    };

    const getMtrLines = () => {
        const lines = [
            {
                level: 4,
                name: '氾濫危険水位',
                color: MTR_BORDER_COLOR.PURPLE,
                val: props.maxValInfo.borderPurpleMax
            },
            {
                level: 3,
                name: '避難判断水位',
                color: MTR_BORDER_COLOR.RED,
                val: props.maxValInfo.borderRedMax
            },
            {
                level: 2,
                name: '氾濫注意水位',
                color: MTR_BORDER_COLOR.YELLOW,
                val: props.maxValInfo.borderYellowMax
            },
            {
                level: 1,
                name: '水防団待機水位',
                color: MTR_BORDER_COLOR.GREEN,
                val: props.maxValInfo.borderGreenMax
            }
        ]
        const sort_lines = lines.sort((a, b) => {
            if (a.val === b.val) {
                return a.level - b.level;
            }
            return a.val - b.val;
        })
        let res = []
        for (let i = 0; i < sort_lines.length; i++) {
            // 最小の基準線、基準値0はここで除外
            if (i < 1 && sort_lines[i].val > 0) {
                res.push(sort_lines[i])
            }
            if (i > 0 && sort_lines[i].val > 0) {
                if (sort_lines[i - 1].val < sort_lines[i].val) {
                    res.push(sort_lines[i])
                }
            }
        }
        return res
    }

    const getAnnotations = () => {
        // 閾値を取得
        const lines = getMtrLines()
        let arry = []
        for (let i = 0; i < lines.length; i++) {
            // 最小の基準線
            if (i < 1) {
                arry.push({
                    y: lines[i].val,
                    borderColor: lines[i].color,
                    label: {
                        borderColor: 'transparent',
                        position: 'left',
                        textAnchor: 'start',
                        offsetX: 0,
                        offsetY: -2,
                        style: {
                            background: 'transparent',
                            color: '#fff',
                        },
                        text: lines[i].name
                    }
                })
            }
            // 最大の基準線
            if (i === lines.length - 1) {
                arry.push(
                    {
                        y: lines[i].val,
                        y2: getAnnotationsMax(),
                        borderColor: lines[i].color,
                        fillColor: lines[i].color,
                    }
                )
            }
            // 中間層の基準線
            if (i > 0) {
                arry.push(
                    {
                        y: lines[i - 1].val,
                        y2: lines[i].val,
                        borderColor: lines[i - 1].color,
                        fillColor: lines[i - 1].color,
                        label: {
                            borderColor: 'transparent',
                            position: 'left',
                            textAnchor: 'start',
                            offsetX: 0,
                            offsetY: -2,
                            style: {
                                background: 'transparent',
                                color: '#fff',
                            },
                            text: lines[i].name
                        }
                    }
                )
            }
        }
        if (props.maxValInfo.borderPurpleMax) {
            setAnnotationMax(props.maxValInfo.borderPurpleMax);
        }
        else if (props.maxValInfo.borderRedMax) {
            setAnnotationMax(props.maxValInfo.borderRedMax);
        }
        else if (props.maxValInfo.borderYellowMax) {
            setAnnotationMax(props.maxValInfo.borderYellowMax);
        }
        else if (props.maxValInfo.borderGreenMax) {
            setAnnotationMax(props.maxValInfo.borderGreenMax);
        }
        else {
            setAnnotationMax(props.maxValInfo.mtrMax + 1);
        }

        setAnnotations({
            yaxis: arry,
            xaxis: [
                {
                    x: props.categoryDates[props.dateArray.length - 1],
                    borderColor: '#0000FF',
                    borderWidth: 1,
                    strokeDashArray: 4,
                    label: {
                        show: false
                    }
                }
            ]
        })
    }

    // チャート情報を格納
    const setChartData = (date_ary, mtr, categoryDates) => {
        const allDateTime = categoryDates;

        // 後ろからdate_ary分を取得
        const mtrVal = mtr.slice(-date_ary.length);
        const diff = allDateTime.length - mtrVal.length;
        // allDateTimeと長さを揃えるためにnullで埋める
        const extendedMtrVal = mtrVal.concat(Array(diff).fill(null));

        setMtrData(extendedMtrVal);

        if (mtr.length > 0) {
            // 河川水位、最大値を取得
            setSeriesMax(props.limitVal + 1)
            if (mtr.length > 0 && mtr.reduce(aryMax) > props.limitVal) {
                setSeriesMax(mtr.reduce(aryMax))
            }
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    useEffect(() => {
        if (props.maxValInfo !== null && props.maxValInfo !== undefined) {
            getAnnotations();
        }
        if (props.dateArray === null || props.dateArray.length === 0 ||
            props.maxMtrDatetime === null || props.maxMtrDatetime.length === 0) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.maxMtrDatetime && props.maxMtrDatetime.length > 0
            && props.categoryDates && props.categoryDates.length > 0) {
            setChartData(props.dateArray, props.maxMtrDatetime, props.categoryDates)
        }
        setIsNoData(false)
        loadingEnd()
    }, [props.dateArray, props.maxMtrDatetime, props.maxValInfo, props.categoryDates]);

    useEffect(() => {
        if (props.dateArray === null || props.dateArray.length === 0
            || props.maxMtrDatetime === null || props.maxMtrDatetime.length === 0) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.dateArray && props.dateArray.length > 0
            && props.maxMtrDatetime && props.maxMtrDatetime.length > 0
            && props.categoryDates && props.categoryDates.length > 0) {
            setChartData(props.dateArray, props.maxMtrDatetime, props.categoryDates)
        }
        setIsNoData(false)
        loadingEnd()

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    const series = useMemo(() => {
        return [
            {
                name: '河川水位(m)',
                type: 'line',
                data: mtrData
            }
        ];
    }, [mtrData]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            toolbar: {
                show: false,
                offsetY: 0
            },
            type: "line",
            stacked: false,
            zoom: {
                enabled: false
            }
        },
        // ポイントの数値表示
        dataLabels: {
            enabled: true,
            enabledOnSeries: [0],
            offsetY: -5,
            style: {
                fontSize: '10px',
                colors: ['white'],
                background: 'transparent'
            },
            background: {
                enabled: false
            },
        },
        stroke: {
            width: [2, 0],
            colors: ['#5c9fed', 'transparent']
        },
        markers: {
            size: [0],
            strokeColors: ['#a9a9a9'],
            strokeWidth: [1],
            hover: {
                size: [3],
                sizeOffset: 0
            }
        },
        plotOptions: {
            bar: {
                dataLabels: {
                    position: 'top' // top, center, bottom
                }
            }
        },
        annotations: annotations,
        xaxis: {
            categories: props.categoryDates,
            labels: {
                rotate: -60,
                rotateAlways: true,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '10px'
                },
                formatter: (value) => {
                    return typeof value === 'string' && value.endsWith(':00') ? value : '';
                }
            }
        },
        yaxis: [
            {
                opposite: true,
                min: 0,
                max: annotationMax + 1,
                seriesName: '河川水位',
                axisTicks: {
                    show: false
                },
                axisBorder: {
                    show: false
                },
                labels: {
                    show: false
                },
                lines: {
                    show: false
                }
            }
        ],
        tooltip: {
            shared: false,
            intersect: false,
            x: {
                show: true,
                formatter: (value, { series, seriesIndex, dataPointIndex, w }) => {
                    return props.categoryDates?.[dataPointIndex] ?? '';
                }
            },
            custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                const value = series[seriesIndex][dataPointIndex];
                return `
                    <div style="
                        background: white;
                        padding: 8px 10px;
                        font-size: 12px;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    ">
                        <span style="
                        display: inline-block;
                        width: 12px;
                        height: 12px;
                        background-color: #5c9fed;
                        border-radius: 50%;
                        "></span>
                        <span>河川水位(m): <strong>${value.toFixed(2)}</strong></span>
                    </div>
                `;
            }
        },
        grid: {
            show: false, // 全体の罫線を消す
            padding: {
                left: 10,
                right: 2,
                top: 0,
                bottom: -20
            }
        },
        legend: {
            show: false,
            height: 0
        }
    }), [props.categoryDates, seriesMax, annotations]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (mtrData && mtrData.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (props.maxValInfo !== null && props.maxValInfo !== undefined) {
            // props.selectTelCode の変更に応じた fetch 再実行
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [props.maxValInfo]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    height: '100%',
                    overflow: 'hidden' // スクロール非表示
                }}
            >
                {/* チャート部分 */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {!props.isReadyFlg ?
                        <Progress />
                        :
                        isNoData ?
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: 'white'
                                }}
                            >
                                対象データがありません
                            </Typography>
                            :
                            <Chart
                                options={options}
                                series={series}
                                type="line"
                                height="100%"
                                width="100%"
                                style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                            />
                    }
                </Box>
            </Box >
        </>
    );
}